<template>
  <section class="login-page" data-test="login-page">
    <div class="login-page__welcome">
      <img
        alt="Welcome to Gyre"
        class="welcome-image"
        src="../assets/registration/welcome-gyre.png"
      />
      <div class="login-footer">
        <ul class="login-footer-list">
          <li class="login-footer-item">
            <a data-test="about-link" href="https://gyreteams.com" role="link">About</a>
          </li>
          <li class="login-footer-item">
            <a data-test="terms-link" href="https://gyreteams.com/terms-of-service" role="link"
              >Terms of Service</a
            >
          </li>
          <li class="login-footer-item">
            <a data-test="contact-link" href="https://gyreteams.com/contact" role="link">Contact</a>
          </li>
        </ul>
      </div>
    </div>
    <article class="login-page-right">
      <img alt="Gyre logo" class="gyre-logo" src="../assets/brand/gyre-icon-lg-bg-green.png" />
      <p class="text-center brand-message">We are now Gyre with a G.</p>
      <div class="login-form-container">
        <h1>Login</h1>
        <form class="login-form" method="post" @submit.prevent="submitForm">
          <div class="login-label-section">
            <label for="email">Email</label>
            <span
              data-test="invalid-email-error"
              v-if="v$.userEmail.$error && v$.userEmail.email.$invalid && attemptedToSubmit"
              class="error-message"
            >
              Invalid email address
            </span>
            <span
              data-test="email-required-error"
              v-if="v$.userEmail.$error && v$.userEmail.required.$invalid && attemptedToSubmit"
              class="error-message"
            >
              Required field
            </span>
            <span data-test="email-not-found-error" v-if="emailNotFound" class="error-message">
              Email address not found
            </span>
            <input
              id="email"
              data-test="email-input"
              v-model.trim="userEmail"
              class="login-text-input"
              name="email"
              placeholder="Email"
              @blur="v$.userEmail.$touch"
              @focus="removeErrorMessage"
            />
            <i class="placeholder-icon"
              ><img alt="Email icon" src="../assets/registration/email-icon.svg"
            /></i>
          </div>
          <div class="login-label-section">
            <label for="password">Password</label>
            <span
              data-test="password-required-error"
              v-if="v$.password.$error && v$.password.required.$invalid && attemptedToSubmit"
              class="error-message"
            >
              Required field
            </span>
            <span
              data-test="password-length-error"
              v-if="v$.password.$error && v$.password.minLength.$invalid && attemptedToSubmit"
              class="error-message"
            >
              Minimum 6 characters long
            </span>
            <span
              data-test="password-incorrect-error"
              v-if="incorrectPassword"
              class="error-message"
              >Incorrect password</span
            >
            <span data-test="locked-out-error" v-if="lockedOut" class="error-message"
              >Account is locked out due to too many failed attempts.<br />Please try again
              later.</span
            >
            <input
              id="password"
              data-test="password-input"
              v-model.trim="password"
              class="login-text-input"
              placeholder="Password"
              type="password"
              @blur="v$.password.$touch"
              @focus="removeErrorMessage"
            />
            <i class="placeholder-icon"
              ><img alt="lock icon" src="../assets/registration/lock-icon.svg"
            /></i>
          </div>
          <div class="checkboxes">
            <div class="checkbox-default">
              <input
                data-test="persist-checkbox"
                type="checkbox"
                name="checkbox"
                v-model="persistLogin"
                id="checkbox"
              />
              <label for="checkbox">Keep me logged in</label>
            </div>
          </div>
          <span data-test="submit-error" v-if="serverErrorOccurred" class="error-message"
            >Something went wrong, please try again later.</span
          >
          <SubmitButton
            data-test="submit-button"
            :isSubmitted="isSubmitted"
            :use-recaptcha="false"
            :disabled="false"
            buttonClass="btn-primary"
            >Login
          </SubmitButton>
        </form>
        <div class="forgot-password">
          <a data-test="forgot-password-link" href="/account/forgotpassword" role="link"
            >Forgot your password?</a
          >
        </div>
      </div>
    </article>
  </section>
</template>

<script lang="ts" setup>
import { computed, ref, onMounted } from "vue";
import { useVuelidate } from "@vuelidate/core";
import { email, minLength, required } from "@vuelidate/validators";
import axios, { AxiosError } from "axios";
import { debug as d } from "debug";
import { useRouter } from "vue-router";
import { LoginApiModel } from "../api-models";
import SubmitButton from "../components/submit-button.vue";

const debug = d("cs.LoginPage");

const userEmail = ref("");
const password = ref("");
const attemptedToSubmit = ref(false);
const persistLogin = ref(true);
const emailNotFound = ref(false);
const serverErrorOccurred = ref(false);
const incorrectPassword = ref(false);
const lockedOut = ref(false);
const submitStatus = ref("");

const router = useRouter();

const rules = {
  userEmail: { required, email },
  password: { required, minLength: minLength(6) },
};

const state = {
  userEmail,
  password,
};

const v$ = useVuelidate(rules, state, { $autoDirty: true });

const isSubmitted = computed(() => submitStatus.value === "OK");

function removeErrorMessage(): void {
  incorrectPassword.value = false;
  emailNotFound.value = false;
  serverErrorOccurred.value = false;
  lockedOut.value = false;
}

function handleLogin(): void {
  const userLoginData = {
    email: userEmail.value.trim(),
    password: password.value.trim(),
    persistLogin: persistLogin.value,
  } as LoginApiModel;
  axios
    .post("/api/auth/login", userLoginData)
    .then((res) => {
      if (res.status === 200) {
        window.location.href = res.data ?? "/home";
      }
    })
    .catch((e: AxiosError) => {
      removeErrorMessage();
      debug(e);
      const statusCode = e.response?.status;
      submitStatus.value = "";
      if (statusCode === 401) {
        incorrectPassword.value = true;
      } else if (statusCode === 403) {
        lockedOut.value = true;
      } else if (statusCode === 404) {
        emailNotFound.value = true;
      } else if (statusCode === 500) {
        serverErrorOccurred.value = true;
      }
    });
}

function submitForm(): void {
  attemptedToSubmit.value = true;
  v$.value.$touch();
  if (v$.value.$invalid) {
    submitStatus.value = "Error";
  } else {
    submitStatus.value = "OK";
    handleLogin();
  }
}

onMounted(() => {
  const { query } = router.currentRoute.value;
  const source = query?.utm_source as string;
  if (source && typeof source === "string" && source.trim() !== "") {
    sessionStorage.setItem("utm_source", source);
  }
});
</script>

<style lang="less" scoped>
@import (reference) "../styles/bootstrap/bootstrap.less";
@import (reference) "../styles/bootstrap/variables.less";

.login-page {
  display: flex;
  min-height: 700px;
  height: 100vh;
  width: 100%;
  padding: 0;

  @media (max-width: 821px) {
    flex-direction: column;
  }
}

.login-page__welcome {
  width: 55%;
  background-color: @registration-background-color;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  background-color: @charismatic-color;

  @media (max-width: 450px) {
    width: 100%;
    height: 35%;
  }
  @media (max-width: 821px) {
    width: 100%;
    height: 35%;
  }
  @media (max-width: 821px) {
    position: unset;
  }
}

.welcome-image {
  width: 100%;
  max-width: 800px;
  aspect-ratio: 1;
  @media (max-width: 821px) {
    width: auto;
    max-width: unset;
    height: 100%;
  }
}

.login-footer {
  position: absolute;
  bottom: 40px;
  width: 55%;
  padding: 0 1rem;

  @media (max-width: 821px) {
    display: none;
  }
}

.login-footer-list {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  list-style: none;
  padding-inline-start: 0;

  .login-footer-item {
    a {
      color: #ffffff;
      font-size: 14px;

      &:hover {
        color: @brand-primary;
        cursor: pointer;
      }
    }
  }
}

.login-page-right {
  width: 45%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;

  @media (max-width: 821px) {
    width: 100%;
    min-height: 800px;
    flex-direction: column-reverse;
    justify-content: flex-end;
    gap: 2rem;
  }

  @media (max-width: 450px) {
    width: 100%;
    margin-top: 1rem;
  }
}

.gyre-logo {
  max-width: 20%;
}

.brand-message {
  padding-top: 10px;
  font-size: 24px;
}

.login-form-container {
  width: 60%;
  max-width: 600px;
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  align-items: left;

  h1 {
    margin-top: 5px;
  }

  @media (max-width: 376px) {
    gap: 1rem;
  }

  @media (max-width: 450px) {
    width: 90%;
  }
  @media (max-width: 821px) {
    margin-top: 2rem;
  }

  a {
    color: @brand-primary;
  }
}

.login-form {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-around;
  gap: 2rem;
  width: 100%;
  height: 90%;
  min-height: 240px;
  @media (max-width: 376px) {
    gap: 1rem;
  }
}

.login-label-section {
  align-items: left;
  width: 100%;
  color: @form-text-color;
  position: relative;

  label {
    font-weight: normal;
  }
}

.login-text-input {
  width: 100%;
  border: none;
  border-bottom: 1px solid rgba(@form-text-color, 0.5);
  background-color: @registration-background-color;
  height: 35px;
  max-width: 100%;
  padding-left: 30px;

  &:focus,
  &:active {
    outline: none;
    border-bottom: 1px solid @form-text-color;
  }

  &::placeholder {
    color: @practical-color;
  }
}

.placeholder-icon {
  position: absolute;
  bottom: 10px;
  left: 10px;

  img {
    height: 14px;
  }
}

.checkboxes {
  width: 100%;

  @media (max-width: 450px) {
    margin-top: 2rem;
  }
}

.checkbox-default {
  margin-left: 1rem;
  width: 95%;
  font-weight: 300;

  label {
    @media (max-width: @screen-sm-min) {
      max-width: 75%;
    }
  }

  .terms-link {
    color: @brand-primary;
  }
}

.btn {
  width: 90%;
  font-weight: bold;
  @media (max-width: 450px) {
    margin-top: 10px;
  }

  img {
    margin: auto 10px 2px 0;
  }
}

.forgot-password {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  margin: 5px 0 20px;

  a {
    color: @brand-primary;
    width: 90%;
  }
}

.error-message {
  color: red;
  display: block;
}

.open-access {
  margin-top: 20px;

  @media (min-height: 950px) {
    position: absolute;
    bottom: 40px;
    left: 0;
    right: 0;
    margin: 0;
  }

  @media (max-width: 821px) {
    bottom: 20px;
  }
}

.register-link-container {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;

  a {
    width: 90%;
  }

  a.btn-default {
    margin-top: 0;
  }
}
</style>
